// Typed client over the ac-admin control-plane Worker (admin.actionconnect.com).
//
// Every call attaches `Authorization: Bearer <token>` and the optional
// `X-Admin-Email` actor header. A 401 throws ApiError with status 401 so the
// app can bounce the user back to Login. Endpoints + payload shapes mirror
// `workers/ac-admin/src/index.ts` exactly.

import { clearCredentials, getCredentials } from "./auth";
import type {
  ActivateSubscriptionInput,
  AuditResponse,
  ContractDetailResponse,
  ContractsResponse,
  ContractTemplateRow,
  CreateLeadInput,
  CreateTenantInput,
  CreateTenantResult,
  FleetContractsResponse,
  HealthResponse,
  ImpersonationScope,
  IssueContractInput,
  IssueContractResult,
  MetricsOverviewResponse,
  PlansResponse,
  SalesStage,
  SetStatusResult,
  TenantDetailResponse,
  TenantHealthResponse,
  TenantRow,
  TenantsResponse,
  TenantStatus,
} from "./types";

export const API_BASE_URL = (
  import.meta.env.VITE_ADMIN_API_URL ?? "https://admin.actionconnect.com"
).replace(/\/+$/, "");

/**
 * Base URL of the tenant edge Worker for impersonation redeem, resolved to the
 * SAME environment the console is talking to — so impersonation never jumps to
 * prod from a staging console. Resolution order:
 *   1. VITE_EDGE_URL — explicit override.
 *   2. Derive from the admin API host: ac-admin* and ac-edge* are sibling workers
 *      in every environment, so swapping the service segment lands on the matching
 *      edge (e.g. ac-admin-staging.…workers.dev → ac-edge-staging.…workers.dev).
 *   3. Fall back to the tenant's own host (correct in prod: admin.actionconnect.com
 *      has no ac-admin segment, so the tenant's custom domain is used).
 */
export function impersonateRedeemBase(tenantHost: string): string {
  const override = import.meta.env.VITE_EDGE_URL;
  if (override) return override.replace(/\/+$/, "");
  try {
    const u = new URL(API_BASE_URL);
    if (u.hostname.includes("ac-admin")) {
      u.hostname = u.hostname.replace("ac-admin", "ac-edge");
      return u.origin;
    }
  } catch {
    /* fall through to tenant host */
  }
  return `https://${tenantHost}`.replace(/\/+$/, "");
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Provide an explicit token (used by the Login flow before it's persisted). */
  token?: string;
  email?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const creds = getCredentials();
  const token = opts.token ?? creds?.token ?? "";
  const email = opts.email ?? creds?.email ?? "";

  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (email) headers["X-Admin-Email"] = email;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method: opts.method ?? "GET",
      headers,
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, `Network error reaching ${API_BASE_URL}. Is the API reachable?`);
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    // On 401, drop the (now-invalid) session token; the router redirects to Login.
    if (res.status === 401) clearCredentials();
    const message =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : undefined) ?? `Request failed (${res.status} ${res.statusText})`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

// ----- health / auth probe ---------------------------------------------------

/** GET /health — used by Login to validate a token before persisting it. */
export function checkHealth(creds?: { token: string; email?: string }): Promise<HealthResponse> {
  // /health itself is unauthenticated on the Worker, but we still send the token
  // so a clearly-wrong base URL or transport failure surfaces during login.
  return request<HealthResponse>("/health", {
    ...(creds?.token ? { token: creds.token } : {}),
    ...(creds?.email ? { email: creds.email } : {}),
  });
}

/**
 * Validate credentials against an AUTHENTICATED endpoint (GET /plans) so a wrong
 * token is rejected at login time rather than on the first real page.
 */
export function validateCredentials(creds: { token: string; email?: string }): Promise<PlansResponse> {
  return request<PlansResponse>("/plans", {
    token: creds.token,
    ...(creds.email ? { email: creds.email } : {}),
  });
}

export interface AdminLoginResult {
  token: string;
  admin: { email: string; name: string };
}

/** Email + password sign-in → an opaque session token (public endpoint). */
export function login(email: string, password: string): Promise<AdminLoginResult> {
  return request<AdminLoginResult>("/auth/login", { method: "POST", body: { email, password } });
}

/** Invalidate the current admin session server-side. */
export function logout(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/auth/logout", { method: "POST" });
}

// ----- plans -----------------------------------------------------------------

export function getPlans(signal?: AbortSignal): Promise<PlansResponse> {
  return request<PlansResponse>("/plans", { ...(signal ? { signal } : {}) });
}

// ----- tenants ---------------------------------------------------------------

export function listTenants(
  params: { status?: TenantStatus | ""; search?: string } = {},
  signal?: AbortSignal,
): Promise<TenantsResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.search) qs.set("search", params.search);
  const q = qs.toString();
  return request<TenantsResponse>(`/tenants${q ? `?${q}` : ""}`, { ...(signal ? { signal } : {}) });
}

export function createTenant(input: CreateTenantInput): Promise<CreateTenantResult> {
  return request<CreateTenantResult>("/tenants", { method: "POST", body: input });
}

// ----- leads (sales pipeline) -----
export function listLeads(signal?: AbortSignal): Promise<{ leads: TenantRow[] }> {
  return request<{ leads: TenantRow[] }>("/leads", { ...(signal ? { signal } : {}) });
}

export function createLead(input: CreateLeadInput): Promise<{ tenant: TenantRow }> {
  return request<{ tenant: TenantRow }>("/leads", { method: "POST", body: input });
}

export function setSalesStage(id: string, stage: SalesStage): Promise<{ ok: boolean; stage: string }> {
  return request<{ ok: boolean; stage: string }>(`/tenants/${encodeURIComponent(id)}/sales-stage`, {
    method: "PATCH",
    body: { stage },
  });
}

/** Provision a lead (or any non-active tenant) — enqueues the provisioning job. */
export function provisionTenant(
  id: string,
  input?: { adminEmail?: string; adminName?: string; planCode?: string },
): Promise<{ tenantId: string; jobId: string; alreadyExisted: boolean }> {
  return request<{ tenantId: string; jobId: string; alreadyExisted: boolean }>(
    `/tenants/${encodeURIComponent(id)}/provision`,
    { method: "POST", body: input ?? {} },
  );
}

export function getTenant(id: string, signal?: AbortSignal): Promise<TenantDetailResponse> {
  return request<TenantDetailResponse>(`/tenants/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function suspendTenant(id: string): Promise<SetStatusResult> {
  return request<SetStatusResult>(`/tenants/${encodeURIComponent(id)}/suspend`, { method: "POST" });
}

export function resumeTenant(id: string): Promise<SetStatusResult> {
  return request<SetStatusResult>(`/tenants/${encodeURIComponent(id)}/resume`, { method: "POST" });
}

/** GET /tenants/:id/health — provisioning state machine + open issues. */
export function getTenantHealth(id: string, signal?: AbortSignal): Promise<TenantHealthResponse> {
  return request<TenantHealthResponse>(`/tenants/${encodeURIComponent(id)}/health`, {
    ...(signal ? { signal } : {}),
  });
}

// ----- fleet metrics (operations dashboard) ----------------------------------

export function getMetricsOverview(signal?: AbortSignal): Promise<MetricsOverviewResponse> {
  return request<MetricsOverviewResponse>("/metrics/overview", { ...(signal ? { signal } : {}) });
}

/** GET /contracts — every contract across the fleet, joined to its tenant. */
export function listAllContracts(signal?: AbortSignal): Promise<FleetContractsResponse> {
  return request<FleetContractsResponse>("/contracts", { ...(signal ? { signal } : {}) });
}

// ----- impersonation (audited, time-boxed) ----------------------------------

export interface ImpersonateResult {
  grantId: string;
  token: string;
}
/** Mint a time-boxed impersonation grant (reason is audited). Returns the
 *  one-time token used to redeem a tenant session at the edge. */
export function impersonateTenant(
  id: string,
  reason: string,
  opts?: { scope?: ImpersonationScope; ttlSeconds?: number },
): Promise<ImpersonateResult> {
  return request<ImpersonateResult>(`/tenants/${encodeURIComponent(id)}/impersonate`, {
    method: "POST",
    body: {
      reason,
      ...(opts?.scope ? { scope: opts.scope } : {}),
      ...(opts?.ttlSeconds ? { ttlSeconds: opts.ttlSeconds } : {}),
    },
  });
}

export interface ImpersonationGrant {
  id: string;
  tenant_id: string;
  platform_user_id: string;
  reason: string;
  expires_at: number;
  revoked_at: number | null;
  created_at: number;
}
export function listImpersonation(
  id: string,
  signal?: AbortSignal,
): Promise<{ grants: ImpersonationGrant[] }> {
  return request<{ grants: ImpersonationGrant[] }>(
    `/tenants/${encodeURIComponent(id)}/impersonation`,
    { ...(signal ? { signal } : {}) },
  );
}
export function revokeImpersonation(grantId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/impersonation/${encodeURIComponent(grantId)}`, {
    method: "DELETE",
  });
}

export function deprovisionTenant(id: string): Promise<{ tenantId: string; jobId: string }> {
  return request<{ tenantId: string; jobId: string }>(`/tenants/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ----- audit -----------------------------------------------------------------

export function getAudit(tenantId?: string, signal?: AbortSignal): Promise<AuditResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  return request<AuditResponse>(`/audit${qs}`, { ...(signal ? { signal } : {}) });
}

// ----- contracts + e-sign ----------------------------------------------------

export function listContracts(tenantId: string, signal?: AbortSignal): Promise<ContractsResponse> {
  return request<ContractsResponse>(`/tenants/${encodeURIComponent(tenantId)}/contracts`, {
    ...(signal ? { signal } : {}),
  });
}

export function issueContract(
  tenantId: string,
  input: IssueContractInput,
): Promise<IssueContractResult> {
  return request<IssueContractResult>(`/tenants/${encodeURIComponent(tenantId)}/contracts`, {
    method: "POST",
    body: input,
  });
}

export function getContract(cid: string, signal?: AbortSignal): Promise<ContractDetailResponse> {
  return request<ContractDetailResponse>(`/contracts/${encodeURIComponent(cid)}`, {
    ...(signal ? { signal } : {}),
  });
}

// ----- reusable contract templates -----
export function listContractTemplates(
  signal?: AbortSignal,
): Promise<{ templates: ContractTemplateRow[] }> {
  return request<{ templates: ContractTemplateRow[] }>("/contract-templates", {
    ...(signal ? { signal } : {}),
  });
}

export function createContractTemplate(input: {
  code: string;
  name: string;
  bodyMd: string;
}): Promise<{ template: ContractTemplateRow }> {
  return request<{ template: ContractTemplateRow }>("/contract-templates", {
    method: "POST",
    body: input,
  });
}

export function updateContractTemplate(
  id: string,
  patch: { name?: string; bodyMd?: string },
): Promise<{ template: ContractTemplateRow }> {
  return request<{ template: ContractTemplateRow }>(`/contract-templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteContractTemplate(id: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/contract-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/** Render a template against a tenant → editable HTML for the Issue modal. */
export function renderContractTemplate(
  id: string,
  body: { tenantId?: string; variables?: Record<string, unknown> },
): Promise<{ title: string; bodyHtml: string; variables: Record<string, unknown> }> {
  return request<{ title: string; bodyHtml: string; variables: Record<string, unknown> }>(
    `/contract-templates/${encodeURIComponent(id)}/render`,
    { method: "POST", body },
  );
}

export function activateSubscription(
  tenantId: string,
  input: ActivateSubscriptionInput,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/tenants/${encodeURIComponent(tenantId)}/activate-subscription`,
    { method: "POST", body: input },
  );
}
